<template>
  <standard-dialog
    v-model="dialog"
    :title="translate(formTitle)"
    :subtitle="editedCategory?.id ? `ID: ${editedCategory.id}` : ''"
    icon="mdi-folder-outline"
    @save="save"
    :disabled="!valid || isSaving"
    :loading="isSaving"
  >
    <v-form v-model="valid">
      <v-container class="container-padding">
        <!-- LANGUAGE TABS -->
        <v-tabs v-model="activeLanguageTab" color="primary" class="mb-4">
          <v-tab v-for="lang in availableLanguages" :key="lang.code">
            {{ lang.text }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeLanguageTab">
          <v-tab-item v-for="lang in availableLanguages" :key="lang.code">
            <v-row class="mb-2">
              <v-col cols="12">
                <text-field
                  v-if="lang.code === 'default'"
                  v-model="editedCategory.name"
                  :rules="[rules.required]"
                  :label="$t('title')"
                  required
                />
                <text-field
                  v-else-if="editedCategory.translations"
                  v-model="editedCategory.translations.find((t) => t.code === lang.code).name"
                  :label="$t('title')"
                />
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
        <v-row>
          <v-col cols="12">
            <autocomplete
              v-model="editedCategory.parent"
              :items="categories.filter((item) => item.id)"
              :label="$t('parent_group')"
              item-text="name"
              item-id="id"
              return-object
            />
          </v-col>
          <v-col cols="12" lg="6">
            <text-field v-model="editedCategory.code" :label="$t('code')" />
          </v-col>
          <v-col cols="12" lg="6">
            <text-field
              type="number"
              v-model="editedCategory.poredak"
              :label="$t('order_m')"
              :hint="$t('hi_num')"
              persistent-hint
              required
              :min="0"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import categoryService from "@/services/category";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "CategoryDialog",
  mixins: [hasValidation, hasSnackbar],
  components: {
    StandardDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    category: {
      type: Object,
      default: () => ({
        name: "",
        code: "",
        poredak: null,
        parent: null,
      }),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editedCategory: {
        name: "",
        code: "",
        poredak: null,
        parent: null,
        translations: [],
      },
      isSaving: false,
      activeLanguageTab: 0,
    };
  },
  computed: {
    availableLanguages() {
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];
      const def = [{ code: "default", text: "default", locale: "default", flag: "", script: "default" }];
      if (restaurantLanguages.length === 0) {
        return def.concat(this.getAvailableLanguages());
      }
      return def.concat(
        this.getAvailableLanguages().filter((lang) => restaurantLanguages.includes(lang.code))
      );
    },
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    formTitle() {
      return !this.isEdit ? this.$t("new_g") : this.$t("edit_g");
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.activeLanguageTab = 0;
        this.editedCategory = Object.assign({}, this.category);
        this.mergeTranslations();
      }
    },
    category: {
      handler(newCategory) {
        this.editedCategory = Object.assign({}, newCategory);
        this.mergeTranslations();
      },
      deep: true,
    },
  },
  methods: {
    mergeTranslations() {
      const existing = this.editedCategory.translations || [];
      this.editedCategory.translations = this.availableLanguages.map((lang) => {
        const found = existing.find((t) => t.code === lang.code);
        return found ? { ...found } : { code: lang.code, name: "" };
      });
    },
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
    async save() {
      if (this.isSaving) return;

      this.isSaving = true;

      try {
        // Map display field names back to API field names (no mapping needed now)
        this.editedCategory.parent_id = this.editedCategory.parent
          ? this.editedCategory.parent.id
          : 0;

        // Filter out "default" from translations — name is already on the root object
        const payload = {
          ...this.editedCategory,
          translations: (this.editedCategory.translations || []).filter(
            (t) => t.code !== "default"
          ),
        };

        if (this.isEdit) {
          await categoryService.updateCategory(this.editedCategory.id, payload);
          this.showSuccessSnackbar(this.$t("category_updated"));
          this.$emit("category-updated", this.editedCategory);
          this.close();
        } else {
          const res = await categoryService.createCategory(payload);
          this.editedCategory.id = res.data.id;
          this.showSuccessSnackbar(this.$t("category_created"));
          this.$emit("category-created", this.editedCategory);
          this.close();
        }
      } catch (error) {
        this.showErrorSnackbar(
          this.$t("error_saving_category") || error?.response?.data?.message
        );
        console.error("Error saving category:", error);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
